import type { SiteConfig } from '@nexa/seo';

/**
 * Giá trị dùng khi chưa khai `VITE_SITE_URL`. Cố tình để một miền `.example` không tồn tại:
 * nếu nó lọt ra production thì hỏng ngay và nhìn thấy được, thay vì trỏ vào một miền thật
 * nào đó rồi âm thầm dồn tín hiệu SEO sang chỗ khác.
 */
export const SITE_URL_FALLBACK = 'https://nexa.example';

export const SITE: SiteConfig = {
  url: import.meta.env.VITE_SITE_URL ?? SITE_URL_FALLBACK,
  name: 'Nexa',
  defaultImage: '/og/default.png',
  /*
   * Màu thanh địa chỉ trên mobile (`<meta name="theme-color">`). Phải là hex TĨNH: thẻ meta này
   * được đọc trước cả CSS nên không dùng được `var(--color-brand-600)`.
   *
   * Giá trị cũ là một sắc indigo do scaffold sinh — KHÔNG phải màu thương hiệu. Giờ là
   * `brand-600` của thiết kế. `script/check-tokens.mjs` canh nó luôn bằng token, nên đổi token
   * mà quên đổi đây là cổng đỏ, không phải lệch âm thầm.
   */
  themeColor: '#173DC9', // check-classes:allow — hex tĩnh bắt buộc, đã được check-tokens.mjs ghim
};

/**
 * Thông tin pháp nhân — đi vào JSON-LD `Organization` và chân trang. Sửa một chỗ, đổi cả site.
 *
 * Ba field dưới đây user chốt 2026-08-20. Nguồn của TỪNG field, vì chúng KHÔNG cùng một nguồn:
 *
 * - `legalName` — thiết kế footer `template.html:1065` ghi `© 2026 NewEra Inc.` và `alt="NewEra Inc."`.
 *   Văn bản pháp lý ghi tên đăng ký đầy đủ (`Công ty Cổ phần NEWERA INC`, ĐKDN 0319377258).
 *   User chốt dùng dạng NGẮN cho cả hai hộ dùng — xem ghi chú "hai hộ dùng, một giá trị" dưới.
 * - `email` — thiết kế footer cột "Liên hệ" (`:1062`). CỐ Ý khác địa chỉ nhận thông báo pháp lý:
 *   đây là liên hệ marketing. **Đừng gộp hai cái.**
 * - `address` — từ văn bản pháp lý, KHÔNG từ thiết kế (thiết kế chỉ ghi "TP. Hồ Chí Minh").
 *   ⚠️ **HIỆN KHÔNG CÓ HỘ DÙNG NÀO.** `jsonld.organization()` chỉ nhận `logo · legalName · sameAs ·
 *   email · telephone` (`packages/seo/src/jsonld.ts` — `OrganizationInput` không khai `address`),
 *   và `HomePage.tsx` truyền đúng năm field đó. Nên chuỗi này **chưa đi tới đâu**: không vào
 *   JSON-LD, không ra chân trang. Giữ lại để lô sau nối dây; muốn nó vào structured data thì phải
 *   mở rộng `OrganizationInput` — chạm `packages/seo`, NGOÀI phạm vi lô D.
 *   Nguồn: đối soát byte-for-byte với 4 văn bản pháp lý do `orc` chạy trên bằng chứng vé #12
 *   (NFC, 91 ký tự). Xem ledger §6.2.
 *
 * `sameAs` và `twitterHandle` ĐÃ BỊ XOÁ (user chốt 2026-08-21): Nexa KHÔNG có tài khoản X, và
 * LinkedIn cũng không. Lô D từng khai cả hai là CHƯA CÓ NGUỒN và để nguyên; giờ đã hỏi được user
 * nên câu trả lời là "không tồn tại", không còn là "chưa biết".
 *   - `sameAs` là danh sách HỒ SƠ CHÍNH THỨC khai với Google. Khai một hồ sơ không tồn tại là gửi
 *     dữ liệu sai; khai danh sách rỗng thì vô ích. Bỏ hẳn trường — `OrganizationInput.sameAs` là
 *     optional và `jsonld.ts:40` bỏ luôn khoá khi `undefined`.
 *   - `twitterHandle` sinh `<meta name="twitter:site">`, thẻ QUY THUỘC card cho một @tài-khoản.
 *     Sai thì có hại. `head.ts:43` đã bọc `if (site.twitterHandle)` nên xoá field là thẻ tự biến mất.
 *   - GIỮ `twitter:card` / `title` / `description` / `image`: chúng chỉ MÔ TẢ nội dung, không quy
 *     thuộc, và hoạt động KHÔNG cần tài khoản. Slack · Discord · Telegram · Zalo đều đọc `twitter:*`
 *     làm dự phòng cho Open Graph ⇒ bỏ chúng là mất link preview ở mọi nơi để đổi lấy không gì.
 *
 * `telephone` ĐÃ BỊ XOÁ (user chốt 2026-08-21): NewEra không có số liên hệ. Lô D khai nó là CHƯA
 * CÓ NGUỒN — tức "chưa tìm thấy". Nay đã tìm, và kết quả mạnh hơn: nhánh `main` (nơi DUY NHẤT có
 * dữ liệu pháp nhân thật) KHÔNG có số nào cả — `site.config.ts` không có trường phone/tel/hotline,
 * và JSON-LD của `main` khai `Organization` bằng `name · url · email · address`, KHÔNG telephone.
 * Tám lượt "số điện thoại" trong văn bản pháp lý đều là TÊN MỘT LOẠI DỮ LIỆU cá nhân của người
 * dùng, không phải số của công ty. `+84 28 7300 0000` là placeholder (bốn số 0 liền).
 *
 * Cùng lớp với `sameAs`: khai một kênh liên hệ không tồn tại là gửi dữ liệu sai — và tệ hơn
 * `sameAs` ở chỗ ai đó có thể BẤM GỌI. `jsonld.ts:23` chỉ bỏ `contactPoint` khi thiếu CẢ email
 * lẫn telephone, nên bỏ số vẫn còn `contactPoint` với `email` — đúng hình dạng `main` đang chạy.
 *
 * `legalName` có MỘT giá trị nhưng HAI hộ dùng muốn hai dạng: chân trang muốn `NewEra Inc.` (đúng
 * thiết kế), còn JSON-LD `Organization.legalName` về ngữ nghĩa muốn TÊN ĐĂNG KÝ đầy đủ. Tách thành
 * `legalName` + `displayName` là thêm một field vào config mà user chưa duyệt ⇒ ĐỀ XUẤT, không tự
 * tách. Ghi ở đây để lựa chọn đó không biến mất.
 */
export const ORGANIZATION = {
  logo: '/logo.svg',
  legalName: 'NewEra Inc.',
  email: 'hello@newera.inc',
  address:
    'Tầng 5, Lim Tower 3, 29A Nguyễn Đình Chiểu, Phường Sài Gòn, Thành phố Hồ Chí Minh, Việt Nam',
} as const;

/**
 * URL của ỨNG DỤNG Nexa — đích của nút "Đăng nhập" ở `Header` và `MobileDrawer`.
 *
 * Khai ở ĐÂY, một chỗ, vì hai component cùng trỏ tới nó. Trước bản này cả hai gõ cứng
 * `https://app.nexa.newera.inc` trong JSX, tức cùng một URL đối ngoại nằm ở hai nơi — đúng hình
 * dạng lỗi đã xảy ra với tên miền ở chân trang: đổi một chỗ, quên chỗ kia, không cổng nào biết.
 *
 * KHÔNG phải env (khác `VITE_SITE_URL`): origin của chính site phải đổi theo môi trường build,
 * còn app hiện chỉ có một địa chỉ. Thêm một biến env mà CI không khai là chừa đường cho nút đăng
 * nhập âm thầm rơi về giá trị mặc định. Cần trỏ sang app staging thì thêm env lúc đó, kèm cổng
 * canh giá trị đã khai.
 *
 * ⚠️ Văn bản pháp lý (`packages/content/src/legal/terms.*.html`) định nghĩa "Website NEXA" là
 * `https://app.nexa.newera.inc/` — hiện LỆCH với hằng này. Văn bản đó đã đăng ký và có cơ chế
 * bằng chứng riêng, nên KHÔNG sửa kèm ở đây: đổi nó là việc của pháp chế, đi qua đường cập nhật
 * nguồn rồi trích lại bằng chứng.
 */
export const APP_URL = 'https://app.nexa.bz';

export interface DesktopDownload {
  /** Đường tải ỔN ĐỊNH — nó 302 sang một URL ký sẵn sống ngắn hạn, nên KHÔNG chép URL đích vào đây. */
  readonly url: string;
  /** Tệp checksum dạng `sha256sum` (`<hash>  <tên tệp>`) của ĐÚNG bản mà `url` đang phục vụ. */
  readonly sha256Url: string;
}

const DIST = 'https://miniapp-dist.nexa.newera.inc/dl';

/**
 * Bản cài Desktop — đích của hai nút trong `#download`. Khai ở ĐÂY, một chỗ, cùng lý do với
 * `APP_URL`: URL đối ngoại giống hệt nhau ở mọi ngôn ngữ.
 *
 * Cả hai đường do bundle-gateway phục vụ, công khai, không xác thực (`GET /dl/{slug}` và
 * `GET /dl/{slug}.sha256`, `Cache-Control: no-store`). Admin thay tệp mà giữ slug thì CẢ HAI tự
 * trỏ sang bản mới — nên ở đây KHÔNG còn hash gõ cứng nào.
 *
 * Bài học có thật: bản đầu (2026-09-28) gõ cứng hash; ngày hôm sau đội app thay tệp và trang in
 * checksum SAI cho cả hai nền tảng. Đừng quay lại hiện hash inline bằng một chuỗi trong code.
 * Đọc tệp `.sha256` bằng JS lúc chạy cũng KHÔNG được: gateway không trả header CORS.
 */
export const DESKTOP_DOWNLOADS: Readonly<Record<'mac' | 'windows', DesktopDownload>> = {
  mac: { url: `${DIST}/macos-installer`, sha256Url: `${DIST}/macos-installer.sha256` },
  windows: { url: `${DIST}/windows-installer`, sha256Url: `${DIST}/windows-installer.sha256` },
};

/**
 * Tên miền để HIỂN THỊ (chân trang), lấy từ `SITE.url` — nguồn duy nhất đã quyết định canonical,
 * hreflang và sitemap.
 *
 * Vì sao là hàm dẫn xuất chứ không phải một chuỗi trong `copy/`: chân trang từng gõ cứng
 * `'Tên miền sẽ chốt sau — dự kiến là một subdomain của newera.inc'`. Đó là khai tên miền ở chỗ
 * THỨ HAI, nên nó trôi khỏi `VITE_SITE_URL` mà không cổng nào biết — và đã trôi thật: bản
 * production chạy trên `nexa.bz` trong khi chân trang vẫn nói tên miền chưa chốt.
 *
 * Parse lỗi thì TRẢ NGUYÊN chuỗi, không ném: một `VITE_SITE_URL` viết thiếu scheme sẽ làm
 * `new URL` ném, và để nó ném ở đây nghĩa là một dòng chữ trong chân trang làm trắng toàn site.
 * Giá trị sai vẫn hiện ra cho người nhìn thấy — đúng tinh thần `SITE_URL_FALLBACK`, nhưng không
 * đánh sập trang để chứng minh điều đó.
 */
export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export const ALLOW_INDEXING = import.meta.env.VITE_ALLOW_INDEXING === 'true';
